import { apiRequest, ApiError, createIdempotencyKey, isTransientStatus, NetworkUnavailableError } from '../api/client'
import type { QueueMutationResult } from '../api/types'
import { captureRequestSession } from '../auth/requestSession'
import {
  deleteOutboxIfIdle,
  deleteOutboxRecords,
  getOutbox,
  listOutbox,
  putOutbox,
  updateOutbox,
  type OutboxRecord,
  type OutboxState,
} from './db'

/** Состояния, в которых действие не отправится само: нужно решение человека или оно уже отклонено. */
export const STUCK_STATES: OutboxState[] = ['auth-required', 'conflict', 'failed', 'obsolete']
/** Столько неудачных попыток подряд считаем «застрявшей» отправкой (совпадает с sync.ts). */
const STUCK_RETRY_ATTEMPTS = 8
export type OutboxClearScope = 'stuck' | 'obsolete' | 'all'

export interface JsonMutation {
  userId: string
  label: string
  method: OutboxRecord['method']
  path: string
  headers?: Record<string, string>
  body?: unknown
  idempotencyKey?: string
}

export interface FileMutation extends Omit<JsonMutation, 'body'> {
  file: File
  fields?: Record<string, string>
  rawResponse?: boolean
}

let lastCreatedAt = 0

function persistentHeaders(headers?: Record<string, string>): Record<string, string> | undefined {
  if (!headers) return undefined
  const safe = Object.fromEntries(
    Object.entries(headers).filter(([name]) => !['authorization', 'cookie', 'proxy-authorization'].includes(name.toLowerCase())),
  )
  return Object.keys(safe).length ? safe : undefined
}

function transportBody(mutation: JsonMutation): unknown {
  if (mutation.method !== 'PATCH' || !/^\/equipment\/[^/]+$/.test(mutation.path) || !mutation.body || typeof mutation.body !== 'object' || Array.isArray(mutation.body)) return mutation.body
  const body = { ...(mutation.body as Record<string, unknown>) }
  delete body.objectId
  delete body.revision
  return body
}

function nowRecord(mutation: JsonMutation): OutboxRecord {
  const now = Math.max(Date.now(), lastCreatedAt + 1)
  lastCreatedAt = now
  return {
    id: crypto.randomUUID(),
    userId: mutation.userId,
    label: mutation.label,
    method: mutation.method,
    path: mutation.path,
    headers: persistentHeaders(mutation.headers),
    bodyType: 'json',
    jsonBody: transportBody(mutation),
    idempotencyKey: mutation.idempotencyKey ?? createIdempotencyKey('offline'),
    state: 'queued',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    nextAttemptAt: now,
  }
}

export async function queueJsonMutation(mutation: JsonMutation, state: OutboxRecord['state'] = 'queued'): Promise<OutboxRecord> {
  const record = nowRecord(mutation)
  record.state = state
  await putOutbox(record)
  await requestBackgroundSync()
  notifyQueueChanged()
  requestForegroundSync()
  return record
}

export async function queueFileMutation(mutation: FileMutation, state: OutboxRecord['state'] = 'queued'): Promise<OutboxRecord> {
  const record = nowRecord(mutation)
  record.state = state
  record.bodyType = 'multipart'
  record.fields = mutation.fields
  record.fileBlob = mutation.file
  record.fileName = mutation.file.name
  record.fileType = mutation.file.type
  record.fileSize = mutation.file.size
  record.jsonBody = undefined
  await putOutbox(record)
  await requestBackgroundSync()
  notifyQueueChanged()
  requestForegroundSync()
  return record
}

/**
 * Ключ зависимости действия: действия по одному заданию (чек-лист, фото, взятие, старт, завершение, дефект из пункта)
 * отправляются строго по порядку, а действия по разным заданиям друг друга не блокируют. Иначе одно застрявшее фото
 * останавливало всю очередь, и работа без сети выглядела сломанной.
 */
export function dependencyKey(record: { path: string; jsonBody?: unknown; fields?: Record<string, string> }): string {
  const path = record.path.split('?')[0]
  const segments = path.split('/').filter(Boolean)
  if (segments[0] === 'work-orders' && segments[1]) return `work-order:${segments[1]}`
  const body = record.jsonBody && typeof record.jsonBody === 'object' ? (record.jsonBody as { workOrderId?: unknown }) : undefined
  const workOrderId = typeof body?.workOrderId === 'string' ? body.workOrderId : record.fields?.workOrderId
  if (workOrderId) return `work-order:${workOrderId}`
  if ((segments[0] === 'defects' || segments[0] === 'equipment') && segments[1]) return `${segments[0]}:${segments[1]}`
  return 'global'
}

function mutationDependencyKey(mutation: JsonMutation | FileMutation): string {
  return dependencyKey({ path: mutation.path, jsonBody: 'body' in mutation ? mutation.body : undefined, fields: 'fields' in mutation ? mutation.fields : undefined })
}

/** Есть ли в очереди более раннее действие той же зависимости: тогда новое действие тоже встаёт в очередь. */
async function hasQueuedPredecessorFor(mutation: JsonMutation | FileMutation): Promise<boolean> {
  const key = mutationDependencyKey(mutation)
  return (await listOutbox(mutation.userId)).some((record) => dependencyKey(record) === key)
}

export async function executeOrQueueJson<T>(mutation: JsonMutation): Promise<QueueMutationResult<T>> {
  const session = captureRequestSession(mutation.userId)
  const key = mutation.idempotencyKey ?? createIdempotencyKey('action')
  const hasQueuedPredecessor = await hasQueuedPredecessorFor(mutation)
  session.assertCurrent()
  let state: OutboxRecord['state'] = session.authRequired ? 'auth-required' : 'queued'
  let reauthenticated = false
  if (navigator.onLine && !hasQueuedPredecessor && !session.authRequired) {
    try {
      const data = await apiRequest<T>(mutation.path, {
        method: mutation.method,
        headers: mutation.headers,
        body: transportBody(mutation),
        idempotencyKey: key,
        authToken: session.token,
      })
      session.assertCurrent()
      return { queued: false, data }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && session.sameUser()) {
        reauthenticated = session.reauthenticated()
        state = reauthenticated ? 'queued' : 'auth-required'
      }
      else if (!(error instanceof NetworkUnavailableError) && !(error instanceof ApiError && isTransientStatus(error.status))) throw error
    }
  }
  session.assertSameUser()
  if (state !== 'auth-required' && !reauthenticated) session.assertCurrent()
  const record = await queueJsonMutation({ ...mutation, idempotencyKey: key }, state)
  return { queued: true, queueId: record.id }
}

export async function executeOrQueueFile<T>(mutation: FileMutation): Promise<QueueMutationResult<T>> {
  const session = captureRequestSession(mutation.userId)
  const key = mutation.idempotencyKey ?? createIdempotencyKey('upload')
  const hasQueuedPredecessor = await hasQueuedPredecessorFor(mutation)
  session.assertCurrent()
  let state: OutboxRecord['state'] = session.authRequired ? 'auth-required' : 'queued'
  let reauthenticated = false
  if (navigator.onLine && !hasQueuedPredecessor && !session.authRequired) {
    const form = new FormData()
    form.append('file', mutation.file, mutation.file.name)
    for (const [name, value] of Object.entries(mutation.fields ?? {})) form.append(name, value)
    try {
      const data = await apiRequest<T>(mutation.path, {
        method: mutation.method,
        headers: mutation.headers,
        body: form,
        idempotencyKey: key,
        authToken: session.token,
        rawResponse: mutation.rawResponse,
      })
      session.assertCurrent()
      return { queued: false, data }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && session.sameUser()) {
        reauthenticated = session.reauthenticated()
        state = reauthenticated ? 'queued' : 'auth-required'
      }
      else if (!(error instanceof NetworkUnavailableError) && !(error instanceof ApiError && isTransientStatus(error.status))) throw error
    }
  }
  session.assertSameUser()
  if (state !== 'auth-required' && !reauthenticated) session.assertCurrent()
  const record = await queueFileMutation({ ...mutation, idempotencyKey: key }, state)
  return { queued: true, queueId: record.id }
}

function isEquipmentConflict(record: OutboxRecord): boolean {
  return record.state === 'conflict' && record.method === 'PATCH' && /^\/equipment\/[^/]+$/.test(record.path)
}

export async function retryOutboxItem(id: string): Promise<OutboxRecord | null> {
  const existing = await getOutbox(id)
  if (!existing) return null
  if (isEquipmentConflict(existing)) throw new Error('Сначала сопоставьте сохранённые значения с текущей версией оборудования')
  if (existing.state !== 'failed' && existing.state !== 'conflict' && existing.state !== 'obsolete') {
    throw new Error('Это действие нельзя повторить в текущем состоянии')
  }
  const updated = await updateOutbox(id, {
    state: 'queued',
    attempts: 0,
    nextAttemptAt: Date.now(),
    lastError: undefined,
    responseStatus: undefined,
    leaseOwner: undefined,
    leaseExpiresAt: undefined,
  })
  await requestBackgroundSync()
  notifyQueueChanged()
  requestForegroundSync()
  return updated
}

export async function discardOutboxItem(id: string) {
  const result = await deleteOutboxIfIdle(id)
  notifyQueueChanged()
  if (result === 'deleted') requestForegroundSync()
  return result
}

export async function resolveEquipmentConflict(
  id: string,
  latestRevision: number,
  mergedBody: Record<string, unknown>,
): Promise<OutboxRecord | null> {
  const existing = await getOutbox(id)
  if (!existing) return null
  if (!isEquipmentConflict(existing)) {
    throw new Error('Этот конфликт нужно исправить в исходном разделе')
  }
  if (!Number.isInteger(latestRevision) || latestRevision < 0) throw new Error('Не удалось определить текущую версию оборудования')
  const patchBody = { ...mergedBody }
  delete patchBody.objectId
  delete patchBody.revision
  const now = Date.now()
  const resolved = await updateOutbox(id, {
    idempotencyKey: createIdempotencyKey('retry'),
    headers: { ...existing.headers, 'If-Match': `"${latestRevision}"` },
    jsonBody: patchBody,
    state: 'queued',
    attempts: 0,
    nextAttemptAt: now,
    lastError: undefined,
    responseStatus: undefined,
    leaseOwner: undefined,
    leaseExpiresAt: undefined,
  })
  await requestBackgroundSync()
  notifyQueueChanged()
  requestForegroundSync()
  return resolved
}

export function isStuckRetry(record: OutboxRecord): boolean {
  return record.state === 'retry' && record.attempts >= STUCK_RETRY_ATTEMPTS
}

export function summarizeQueue(records: OutboxRecord[]) {
  return {
    total: records.length,
    pending: records.filter((record) => ['queued', 'syncing', 'retry'].includes(record.state)).length,
    attention: records.filter((record) => ['auth-required', 'conflict', 'failed'].includes(record.state) || isStuckRetry(record)).length,
    obsolete: records.filter((record) => record.state === 'obsolete').length,
  }
}

export async function getQueueSummary(userId: string) {
  return summarizeQueue(await listOutbox(userId))
}

/**
 * Массовая очистка очереди устройства: «stuck» — всё, что не отправится само (конфликты, отказы, отклонённые,
 * зависшие повторы), «obsolete» — только отклонённые сервером, «all» — вся очередь. Действие, отправляемое прямо
 * сейчас, не трогается. Используется со страницы синхронизации и по команде администратора с сервера.
 */
export async function clearOutbox(userId: string, scope: OutboxClearScope, before?: number): Promise<number> {
  const deleted = await deleteOutboxRecords(userId, (record) => {
    if (before !== undefined && record.createdAt >= before) return false
    if (scope === 'all') return true
    if (scope === 'obsolete') return record.state === 'obsolete'
    return STUCK_STATES.includes(record.state) || isStuckRetry(record)
  })
  notifyQueueChanged()
  if (deleted > 0) requestForegroundSync()
  return deleted
}

export async function requestBackgroundSync(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return
    if ('sync' in registration) {
      await (registration as ServiceWorkerRegistration & { sync: { register(tag: string): Promise<void> } }).sync.register('amalthea-outbox')
    }
  } catch {
    // Foreground online/focus retry remains available when Background Sync is unavailable.
  }
}

export function notifyQueueChanged() {
  window.dispatchEvent(new CustomEvent('amalthea:queue-changed'))
}

function requestForegroundSync() {
  window.dispatchEvent(new CustomEvent('amalthea:sync-requested'))
}
